import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Briefcase, Plus, Save, Trash2, Users } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api, APIError, extractErrorMessage } from '../../api/client'

export default function CompanyDetailScreen() {
  const navigate = useNavigate()
  const { companyId } = useParams()
  const [company, setCompany] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.get(`/api/b2b/companies/${companyId}`)
      .then((data) => {
        if (cancelled) return
        setCompany(data)
        setName(data.name || '')
        setDescription(data.description || '')
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof APIError && err.status === 404 ? 'Company not found.' : 'Could not load company.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [companyId])

  const dirty = company && (name !== company.name || description !== (company.description || ''))

  const handleSave = async () => {
    setSaveError('')
    if (!name.trim()) { setSaveError('Name is required.'); return }
    setSaving(true)
    try {
      const updated = await api.patch(`/api/b2b/companies/${companyId}`, {
        name: name.trim(),
        description: description.trim(),
      })
      setCompany(updated)
    } catch (err) {
      setSaveError(err instanceof APIError ? extractErrorMessage(err.data) : 'Could not save changes.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/api/b2b/companies/${companyId}`)
      navigate('/b2b/companies', { replace: true })
    } catch {
      setDeleting(false)
      setShowDelete(false)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto"><p className="text-sm text-ink-3">Loading…</p></div>
      </AppShell>
    )
  }

  if (error || !company) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto">
          <Link to="/b2b/companies" className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error || 'Company not found.'}
          </div>
        </div>
      </AppShell>
    )
  }

  const jobs = company.jobs || []

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
        <Link to="/b2b/companies" className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
          <ArrowLeft className="w-4 h-4" /> Back to companies
        </Link>

        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-ink-1">{company.name}</h1>

        {/* Editable details */}
        <div className="mt-6 bg-white rounded-3xl shadow-card border border-black/5 p-6 space-y-4">
          {saveError && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{saveError}</div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">Company name</label>
            <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 focus-within:border-accent transition-colors">
              <input
                type="text"
                className="flex-1 text-[15px] outline-none bg-transparent"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">Description</label>
            <textarea
              rows={4}
              className="w-full text-[15px] outline-none bg-white rounded-2xl border border-black/10 px-4 py-3.5 focus:border-accent transition-colors resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Jobs at this company */}
        <div className="mt-10 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3">Jobs ({jobs.length})</h2>
          <Link
            to={`/b2b/jobs/new?company=${company.id}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline"
          >
            <Plus className="w-4 h-4" /> New job
          </Link>
        </div>
        <div className="mt-4">
          {jobs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-8 text-center">
              <p className="text-sm text-ink-3">No jobs linked to this company yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/b2b/jobs/${job.id}`}
                  className="bg-white rounded-2xl shadow-card hover:shadow-card-hover p-5 flex items-center gap-4 transition"
                >
                  <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-ink-1 truncate">{job.title}</h3>
                    <p className="text-xs text-ink-3 mt-1">
                      {[job.department, job.location].filter(Boolean).join(' • ') || 'No details yet'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-ink-3 text-sm">
                    <Users className="w-4 h-4" />
                    <span>{job.candidate_count}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Danger zone */}
        <div className="mt-12 rounded-2xl border border-red-100 bg-red-50/40 p-5">
          <h2 className="text-xs font-bold uppercase tracking-wider text-red-700">Danger zone</h2>
          <p className="mt-1 text-sm text-ink-2">
            Delete this company. Its jobs are kept but unlinked (they lose their company).
          </p>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="mt-3 inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-100 font-semibold px-4 min-h-[44px] rounded-2xl transition text-sm"
          >
            <Trash2 className="w-4 h-4" /> Delete company
          </button>
        </div>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4" style={{ background: 'rgba(15,23,42,0.55)' }}>
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-ink-1">Delete this company?</h3>
                <p className="mt-1 text-sm text-ink-2">
                  <strong>{company.name}</strong> will be removed. Its {jobs.length} job{jobs.length === 1 ? '' : 's'} are kept but unlinked.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowDelete(false)} disabled={deleting}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-2 hover:bg-canvas">Cancel</button>
              <button type="button" onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}
